import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { studio } from "@/data/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/chi-siamo", label: "Chi Siamo" },
  { to: "/servizi", label: "Servizi" },
  { to: "/strumentazione", label: "Strumentazione" },
  { to: "/lavori", label: "Lavori" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 border border-slate-900 flex items-center justify-center font-serif text-lg text-slate-900 group-hover:bg-slate-900 group-hover:text-amber-200 transition-colors">
              C
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-slate-900">
                Studio Cecconato
              </div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500">
                Geom. Michele · Villorba TV
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors link-underline ${
                    isActive ? "text-amber-800" : "text-slate-700 hover:text-slate-900"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <a
            href={studio.phoneHref}
            data-testid="navbar-phone-cta"
            className="hidden lg:inline-flex items-center gap-2 bg-slate-900 hover:bg-amber-800 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors"
          >
            <Phone size={14} />
            Chiama lo studio
          </a>

          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-300 bg-white"
            aria-label="Apri menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-base font-medium ${
                    isActive ? "text-amber-800" : "text-slate-800"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href={studio.phoneHref}
              data-testid="navbar-mobile-phone"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-full"
            >
              <Phone size={14} /> Chiama lo studio
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
